import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  authRequest,
  authGoogleRequest,
  passwordRequest,
  validationRequest,
  systemSigninRequest,
  signUpRequest,
  newSigninRequest,
} from 'core/services/authentication';
import { JWT_TOKEN, REFRESH_TOKEN } from '../../../constants';
import { IAuthResponse, IValidateResponse, ValidateState } from 'types/session/auth';
import { dialogueRequest, errorRequest, searchRequest, tokenRequest } from 'core/services/product';
import { ITokenActionResponse } from 'types/action/player';
import { getRequest } from 'core/services/admin';
import { ISessionReducer, logout } from 'core/slices/session';
import { hydrate } from 'core/actions/product/state';
import { selectSession } from 'core/selectors/product/session';
import { Role } from 'types/domain/role';
import { RootState } from 'types';

function* auth(
  action: PayloadAction<IAuthResponse> | PayloadAction<IValidateResponse> | PayloadAction<ITokenActionResponse> | any,
) {
  yield handleAuthResponse(action.payload);
}

export function* handleAuthResponse(payload) {
  //if (!localStorage || !localStorage.setItem) return;
  try {
    if (payload.token && localStorage) yield call([localStorage, localStorage.setItem], JWT_TOKEN, payload.token);
    if (payload.refresh && localStorage)
      yield call([localStorage, localStorage.setItem], REFRESH_TOKEN, payload.refresh);
    if (payload.cookies) yield all(payload.cookies.map(c => call(addCookie, c)));
  } catch (e) {
    yield put(
      yield call(errorRequest, {
        code: 0,
        message: 'AuthResponse error - could not set token or cookies',
        stackTrace: e.toString(),
      }),
    );
  }
}

function addCookie(cookie) {
  document.cookie = `${cookie}SameSite=None; Secure`;
}

function* rejected() {
  const role: Role = yield select((state: RootState) => state.session.role);

  if (role > Role.LEARNER) yield put(yield call(logout));
}
function* unauthorisedRequestsSaga(action: any) {
  yield put(yield call(logout));
}

function* dialogueRequestSaga(action) {
  if (action.payload?.auth) yield handleAuthResponse(action.payload.auth);
}

function* logoutSaga() {
  if (!localStorage || !localStorage.removeItem) return;

  yield call([localStorage, localStorage.removeItem], JWT_TOKEN);
  yield clearCookies();
}

export function* clearCookies() {
  yield all(
    document.cookie
      .split(';')
      .map(c =>
        call(addCookie, c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')),
      ),
  );
}

function* hydrateSaga() {
  const session: ISessionReducer = yield select(selectSession);
  if (session.validate.state !== ValidateState.VALIDATED) yield validate();

  var domain = window.location.hostname.split('.')[0];
  document.cookie = `subdomain=${domain};path=/;domain=.how.io`;
}

function* validate() {
  const token = yield call([localStorage, localStorage.getItem], JWT_TOKEN);
  const refresh = yield call([localStorage, localStorage.getItem], REFRESH_TOKEN);
  if (token && refresh) yield put(yield call(validationRequest, { refresh, token }));
}

function* watchAuthFulfilled() {
  yield takeLatest(
    [
      authRequest.fulfilled,
      authGoogleRequest.fulfilled,
      searchRequest.fulfilled,
      validationRequest.fulfilled,
      passwordRequest.fulfilled,
      systemSigninRequest.fulfilled,
      signUpRequest.fulfilled,
      newSigninRequest.fulfilled,
      //domainRequest.fulfilled,
    ],
    auth,
  );
}

function* watchDialogueRequestFulfilled() {
  yield takeEvery(dialogueRequest.fulfilled, dialogueRequestSaga);
}

function* watchAuthRejected() {
  yield takeLatest(
    [authRequest.rejected, authGoogleRequest.rejected, validationRequest.rejected, tokenRequest.rejected],
    rejected,
  );
}

function* watchLogout() {
  yield takeLatest(logout, logoutSaga);
}

function* watchUnAuthorisedRequests() {
  yield takeLatest([getRequest.rejected], unauthorisedRequestsSaga);
}

function* watchHydrate() {
  yield takeLatest(hydrate, hydrateSaga);
}

export function* authSaga() {
  yield all([
    fork(watchAuthFulfilled),
    fork(watchAuthRejected),
    fork(watchUnAuthorisedRequests),
    fork(watchLogout),
    fork(watchDialogueRequestFulfilled),
    fork(watchHydrate),
  ]);
}
